import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Redirect } from "react-router-dom";
import { Dayjs } from "dayjs";
import { ArrowLeft, ListFilter, Plus, Upload } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { useSelectRealUser } from "@shared/utils/hooks/misc.ts";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import RangePicker from "common/DatePicker/RangePicker.tsx";
import Button from "common/Buttons/Button.tsx";
import {
  NEW_LOOK_ICON_STROKE,
  NEW_LOOK_PAGE_GUTTER,
  NEW_LOOK_SECTION_GAP,
} from "common/NewLook/layout.ts";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { DASHBOARD_PATH, MOBILE_SEARCH } from "common/constants.ts";
import { useGetFinanceSummaryQuery } from "app/services/finance.service";
import {
  FinanceCurrency,
  FinanceDateRange,
  FinanceEntriesFilter,
  FinanceGranularity,
} from "features/finance/types";
import {
  CATEGORY_TO_LABEL,
  DIRECTION_TO_LABEL,
  FINANCE_CURRENCIES,
  FINANCE_DATE_FORMAT,
  getDefaultFinanceRange,
  SOURCE_TO_LABEL,
} from "features/finance/constants";
import {
  filterSummaryRows,
  getFinanceExchangeLabel,
  getFinanceTotals,
  getGranularity,
  getMonthRange,
  getPeriodsInRange,
  getPeriodTotals,
  getPreviousRange,
  hasEntriesFilter,
  isSuperuser,
} from "features/finance/utils";
import FinanceKpis from "features/finance/FinanceKpis";
import FinancePeriodChart from "features/finance/FinancePeriodChart";
import FilterChip from "features/finance/FilterChip";
import FinanceBreakdown from "features/finance/FinanceBreakdown";
import FinanceEntriesTable from "features/finance/FinanceEntriesTable";
import UploadStatementModal from "features/finance/UploadStatementModal";
import AddEntryModal from "features/finance/AddEntryModal";
import FinanceRulesDrawer from "features/finance/FinanceRulesDrawer";
import useRequestErrorMessage from "features/finance/useRequestErrorMessage";
import { FINANCE_PAGE_ID } from "testIds.ts";

const FinancePage = () => {
  const realUser = useSelectRealUser();
  if (!realUser) return null;
  if (!isSuperuser(realUser)) return <Redirect to={DASHBOARD_PATH} />;
  return <FinanceDashboard />;
};

export default FinancePage;

type FinanceDates = [Dayjs, Dayjs];

const FinanceDashboard = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const [dates, setDates] = useState<FinanceDates>(getDefaultFinanceRange);
  const [zoomedFrom, setZoomedFrom] = useState<FinanceDates>();
  const [selectedDay, setSelectedDay] = useState<string>();
  const [currency, setCurrency] = useState(FinanceCurrency.USD);
  const [entriesFilter, setEntriesFilter] = useState<FinanceEntriesFilter>({});
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isAddEntryOpen, setIsAddEntryOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const entriesRef = useRef<HTMLDivElement>(null);

  const range: FinanceDateRange = useMemo(
    () => ({
      start_date: dates[0].format(FINANCE_DATE_FORMAT),
      end_date: dates[1].format(FINANCE_DATE_FORMAT),
    }),
    [dates]
  );
  const granularity = getGranularity(range);
  const previousRange = useMemo(() => getPreviousRange(range), [range]);
  const { currentData, isFetching, isError, error } = useGetFinanceSummaryQuery(
    { ...range, currency, granularity }
  );
  const { currentData: previousData } = useGetFinanceSummaryQuery({
    ...previousRange,
    currency,
  });

  useRequestErrorMessage(isError, error);

  const summary = currentData ?? EMPTY_SUMMARY;
  const isFiltered = hasEntriesFilter(entriesFilter);
  const periods = useMemo(
    () => getPeriodsInRange(range, granularity),
    [range, granularity]
  );
  const totals = useMemo(() => getFinanceTotals(summary), [summary]);
  const previousTotals = useMemo(
    () => previousData && getFinanceTotals(previousData),
    [previousData]
  );
  const periodTotals = useMemo(
    () => getPeriodTotals(filterSummaryRows(summary, entriesFilter), periods),
    [summary, entriesFilter, periods]
  );

  const applyDates = (value: FinanceDates, from?: FinanceDates) => {
    setDates(value);
    setZoomedFrom(from);
    setSelectedDay(undefined);
  };

  const onDatesChange = (value: [Dayjs | null, Dayjs | null] | null) => {
    if (value?.[0] && value[1])
      applyDates([value[0].startOf("day"), value[1].startOf("day")]);
  };

  const onPeriodClick = (period: string) => {
    if (granularity === FinanceGranularity.MONTH) {
      applyDates(
        getMonthRange(period, getDefaultFinanceRange()[1]),
        zoomedFrom ?? dates
      );
      return;
    }
    const isSameDay = period === selectedDay;
    setSelectedDay(isSameDay ? undefined : period);
    if (!isSameDay)
      entriesRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  const getFilterLabel = () =>
    [
      entriesFilter.direction && t(DIRECTION_TO_LABEL[entriesFilter.direction]),
      entriesFilter.category && t(CATEGORY_TO_LABEL[entriesFilter.category]),
      entriesFilter.source && t(SOURCE_TO_LABEL[entriesFilter.source]),
      entriesFilter.exchange && getFinanceExchangeLabel(entriesFilter.exchange),
    ]
      .filter(Boolean)
      .join(" · ");

  const iconProps = { size: 16, strokeWidth: NEW_LOOK_ICON_STROKE };
  const isDaily = granularity === FinanceGranularity.DAY;

  return (
    <Page data-testid={FINANCE_PAGE_ID}>
      <Toolbar>
        <Controls>
          <RangePicker
            value={dates}
            onChange={onDatesChange}
            onPresetsClick={onDatesChange}
            allowClear={false}
            maxValue={getDefaultFinanceRange()[1].endOf("day")}
            wrapperStyle={{ width: 260 }}
            aria-label={t("finance.toolbar.date_range")}
          />
          <SegmentTabs
            options={FINANCE_CURRENCIES}
            value={currency}
            onChange={setCurrency}
            ariaLabel={t("finance.toolbar.currency")}
          />
        </Controls>
        <Actions>
          <Button
            onClick={() => setIsRulesOpen(true)}
            icon={<ListFilter {...iconProps} />}
          >
            {t("finance.toolbar.rules")}
          </Button>
          <Button
            onClick={() => setIsUploadOpen(true)}
            icon={<Upload {...iconProps} />}
          >
            {t("finance.toolbar.upload")}
          </Button>
          <Button
            type="primary"
            onClick={() => setIsAddEntryOpen(true)}
            icon={<Plus {...iconProps} />}
          >
            {t("finance.toolbar.add_entry")}
          </Button>
        </Actions>
      </Toolbar>
      <FinanceKpis
        totals={totals}
        previousTotals={previousTotals}
        previousRange={previousRange}
        currency={currency}
        periodLabel={t(isDaily ? "finance.kpis.days" : "finance.kpis.months", {
          count: periods.length,
        })}
        loading={isFetching && !currentData}
      />
      <Panel
        title={t(isDaily ? "finance.chart.daily_title" : "finance.chart.title")}
        extra={
          isFiltered ? (
            <FilterChip
              compact={true}
              clearLabel={t("finance.chart.clear_filter")}
              onClear={() => setEntriesFilter({})}
            >
              {getFilterLabel()}
            </FilterChip>
          ) : (
            <Hint>
              {t(
                isDaily
                  ? "finance.chart.click_day"
                  : "finance.chart.click_month"
              )}
            </Hint>
          )
        }
        actions={
          zoomedFrom && (
            <Button
              size="small"
              onClick={() => applyDates(zoomedFrom)}
              icon={<ArrowLeft size={14} strokeWidth={NEW_LOOK_ICON_STROKE} />}
            >
              {t("finance.chart.back")}
            </Button>
          )
        }
      >
        <FinancePeriodChart
          periods={periodTotals}
          granularity={granularity}
          currency={currency}
          loading={isFetching}
          selectedPeriod={selectedDay}
          filter={entriesFilter}
          onPeriodClick={onPeriodClick}
        />
      </Panel>
      <FinanceBreakdown
        rows={summary}
        currency={currency}
        loading={isFetching && !currentData}
        filter={entriesFilter}
        onSelect={setEntriesFilter}
      />
      <EntriesAnchor ref={entriesRef}>
        <FinanceEntriesTable
          range={range}
          currency={currency}
          filter={entriesFilter}
          onFilterChange={setEntriesFilter}
          day={selectedDay}
          onDayChange={setSelectedDay}
        />
      </EntriesAnchor>
      <UploadStatementModal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
      />
      <AddEntryModal
        open={isAddEntryOpen}
        onClose={() => setIsAddEntryOpen(false)}
        defaultCurrency={currency}
      />
      <FinanceRulesDrawer
        open={isRulesOpen}
        onClose={() => setIsRulesOpen(false)}
      />
    </Page>
  );
};

const EMPTY_SUMMARY: never[] = [];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_SECTION_GAP}px;
  padding: 16px ${NEW_LOOK_PAGE_GUTTER}px 32px;
  min-width: 0;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 12px 16px 24px;
    gap: 12px;
  }
`;
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;
const Hint = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 700px) {
    display: none;
  }
`;
const EntriesAnchor = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  scroll-margin-top: 80px;
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  @media (max-width: ${MOBILE_SEARCH}) {
    width: 100%;
    .ant-btn {
      flex: 1 1 auto;
    }
  }
`;
